<template>
    <div class="h-full flex flex-col p-4 pt-6">
        <multi-product-creator v-if="MultiCreateFeature.isEnabled()"/>
        <div class="flex justify-between py-2">
            <UButton class="w-fit" data-cy="product-create-button" @click="showProductEditorPopup()">Nytt produkt</UButton>
            <UInput
                v-model="searchText"
                icon="i-ph-magnifying-glass"
                placeholder="Søk etter produkt"
                class="grow max-w-[300px] truncate"
                autocomplete="off"
            />
        </div>
        <ag-grid-vue
            class="w-full h-full py-4"
            :theme="agTheme"
            :column-defs="columns"
            :row-data="products"
            :quick-filter-text="searchText"
            :grid-options="gridOptions"
        />
    </div>
</template>

<script setup lang="ts">
import {useProducts} from '@/products/product.service'
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {Product} from '@/products/product.types'
import {inject, ref, Ref, shallowRef} from 'vue'
import {CellClassParams, ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, RowDoubleClickedEvent, ValueGetterParams} from 'ag-grid-community'
import {formatCurrency} from '@/services/formatter.service'
import AccountNoCell from '@/bookkeeping-account/AccountNoCell.vue'
import ProductEditorPopup from '@/products/ProductEditorPopup.vue'
import {useOverlay} from '@nuxt/ui/composables/useOverlay'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import {MultiCreateFeature} from '@/subaccount/feature-toggles'
import MultiProductCreator from '@/components/MultiProductCreator.vue'

const panelId: Ref<number> | undefined = inject('panelId')
const {products} = useProducts()

const searchText = ref('')

const gridApi = shallowRef<GridApi>()
const columns: ColDef[] = [
    {
        headerName: 'Navn',
        field: 'name',
        sortable: true,
        filter: true,
        flex: 1
    },
    {
        headerName: 'Produktkode',
        field: 'code',
        sortable: true,
        filter: true
    },
    {
        headerName: 'Pris',
        field: 'retailPrice',
        sortable: true,
        filter: true,
        headerClass: 'header-right',
        cellClass: ({data}: CellClassParams) => {
            return 'text-right' + (data.active ? '' : ' text-base-content/50')
        },
        cellRenderer: (params: ICellRendererParams) => {
            return formatCurrency(params.data?.retailPrice)
        }
    },
    {
        headerName: 'Inntektskonto',
        field: 'accountNo',
        headerClass: 'header-right',
        cellRenderer: AccountNoCell,
        cellClass: ({data}: CellClassParams) => {
            return 'text-right' + (data.active ? '' : ' text-base-content/50')
        }
    },
    {
        headerName: 'Aktiv',
        field: 'active',
        valueGetter: (params: ValueGetterParams) => params.data.active,
        cellRenderer: (params: ICellRendererParams) => {
            return params.value ?
                '<i class="inline-block text-primary i-ph-check" />'
                : '<i class="inline-block text-error i-ph-x" />'
        },
        cellEditor: 'agCheckboxCellEditor'
    }
]

const overlay = useOverlay()
const productModal = overlay.create(ProductEditorPopup)

const gridOptions: GridOptions = {
    getRowId: (params: { data: Product }) => params.data.uuid,
    onGridReady: ({api}: GridReadyEvent) => {
        gridApi.value = api
    },
    animateRows: true,
    context: {
        panelId
    },
    onRowDoubleClicked(event: RowDoubleClickedEvent<Product>) {
        showProductEditorPopup(event.data as Product)
    },
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => 'Ingen produkter'
    },
}

const showProductEditorPopup = (product?: Product) => {
    productModal.open({product})
}

</script>

<style scoped>

</style>